import React, { useState } from "react"
import {
  Box,
  Input,
  Button,
  Flex,
  Label,
  Text,
  Heading,
} from "@theme-ui/components"
import { Textarea, Checkbox } from "theme-ui"
import axios from "axios"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import ArrowRight from "../assets/images/arrow-right.svg"
import { InboundLink } from "./link"

const BaseContactForm = ({
  onSuccess,
  onError,
  buttonText = "INVIA RICHIESTA",
  showCompanyField = true,
  showCityField = true,
  showProvinceField = true,
  showMessageField = true,
  compact = false,
  variant = "primary",
  customStyles = {},
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const isBrowser = typeof window !== "undefined"

  const [formData, setFormData] = useState({
    email: "",
    nomeCognome: "",
    ragioneSociale: "",
    telefono: "",
    comune: "",
    provincia: "",
    messaggio: "",
    accordoPrivacyCookies: false,
    accordoNewsLetter: false,
    urlPagina: isBrowser && window.location.href,
  })

  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(null)

  const sendMail = async event => {
    event.preventDefault()

    const result = await executeRecaptcha("dynamicAction")

    setLoading(true)
    const data = formData

    if (result)
      axios
        .post("/.netlify/functions/sendContactMail", data)
        .then(function (response) {
          setSuccess(true)
          setLoading(false)

          if (typeof window !== "undefined") {
            window.dataLayer = window.dataLayer || []

            window.dataLayer.push({
              event: "form_submit_contact",
              formType: "Contact",
              pageURL: window.location.href,
            })
          }

          if (onSuccess) {
            onSuccess()
          }
        })
        .catch(function (error) {
          setLoading(false)
          setSuccess(false)

          if (onError) {
            onError(error)
          }
        })
    else {
      setLoading(false)
      setSuccess(false)

      if (onError) {
        onError(new Error("reCAPTCHA verification failed"))
      }
    }
  }

  const isLight = variant === "light"
  const isDark = variant === "dark"
  const isPrimary = variant === "primary"

  const getInputVariant = () => {
    if (isLight) return "inputs.light"
    if (isDark) return "inputs.dark"
    return "inputs.primary"
  }

  const getButtonVariant = () => {
    if (isLight) return "buttons.primaryFormLight"
    if (isDark) return "buttons.primaryFormEmpty"
    return "buttons.primaryForm"
  }

  const getTextColor = () => {
    if (isLight) return "dark"
    if (isDark) return "dark"
    return "light"
  }

  const getArrowVariant = () => {
    if (isLight) return "svg.arrow.dark"
    if (isDark) return "svg.arrow.light"
    return "svg.arrow.red"
  }

  return (
    <Box as="form" onSubmit={sendMail} sx={{ ...customStyles.form }}>
      <Box>
        <Grid
          sx={{
            flexWrap: "wrap",
            flexDirection: "column",
            ...customStyles.grid,
          }}
          gap={[0, 0, 0, 0]}
        >
          <Flex sx={{ flexWrap: "wrap" }}>
            <Box
              sx={{
                pr: compact ? 0 : [0, 1, 1, 1],
                pb: [3, 3, 3, 3],
                width: compact ? "100%" : ["100%", "50%"],
                ...customStyles.nameField,
              }}
            >
              <Input
                onChange={e =>
                  setFormData(prevState => ({
                    ...prevState,
                    nomeCognome: e.target.value,
                  }))
                }
                name="nome cognome"
                placeholder="Nome e Cognome*"
                variant={getInputVariant()}
                required
                sx={customStyles.input}
              />
            </Box>
            {showCompanyField && (
              <Box
                sx={{
                  pl: compact ? 0 : [0, 1, 1, 1],
                  pb: [3, 3, 3, 3],
                  width: compact ? "100%" : ["100%", "50%"],
                  ...customStyles.companyField,
                }}
              >
                <Input
                  onChange={e =>
                    setFormData(prevState => ({
                      ...prevState,
                      ragioneSociale: e.target.value,
                    }))
                  }
                  name="ragione sociale"
                  placeholder="Ragione Sociale*"
                  variant={getInputVariant()}
                  required
                  sx={customStyles.input}
                />
              </Box>
            )}
          </Flex>
          <Flex sx={{ flexWrap: "wrap" }}>
            <Box
              sx={{
                pr: compact ? 0 : [0, 1, 1, 1],
                pb: [3, 3, 3, 3],
                width: compact ? "100%" : ["100%", "50%"],
                ...customStyles.emailField,
              }}
            >
              <Input
                onChange={e =>
                  setFormData(prevState => ({
                    ...prevState,
                    email: e.target.value,
                  }))
                }
                name="email"
                type="email"
                placeholder="Email*"
                variant={getInputVariant()}
                required
                sx={customStyles.input}
              />
            </Box>
            <Box
              sx={{
                pl: compact ? 0 : [0, 1, 1, 1],
                pb: [3, 3, 3, 3],
                width: compact ? "100%" : ["100%", "50%"],
                ...customStyles.phoneField,
              }}
            >
              <Input
                onChange={e =>
                  setFormData(prevState => ({
                    ...prevState,
                    telefono: e.target.value,
                  }))
                }
                name="telefono"
                type="text"
                placeholder="Telefono*"
                variant={getInputVariant()}
                required
                sx={customStyles.input}
              />
            </Box>
          </Flex>
          {(showCityField || showProvinceField) && (
            <Flex sx={{ flexWrap: "wrap" }}>
              {showCityField && (
                <Box
                  sx={{
                    pr: compact ? 0 : [0, 1, 1, 1],
                    pb: [3, 3, 3, 3],
                    width: compact ? "100%" : ["100%", showProvinceField ? "50%" : "100%"],
                    ...customStyles.cityField,
                  }}
                >
                  <Input
                    onChange={e =>
                      setFormData(prevState => ({
                        ...prevState,
                        comune: e.target.value,
                      }))
                    }
                    name="city"
                    placeholder="Comune*"
                    variant={getInputVariant()}
                    required
                    sx={customStyles.input}
                  />
                </Box>
              )}
              {showProvinceField && (
                <Box
                  sx={{
                    pl: compact ? 0 : [0, 1, 1, 1],
                    pb: [3, 3, 3, 3],
                    width: compact ? "100%" : ["100%", showCityField ? "50%" : "100%"],
                    ...customStyles.provinceField,
                  }}
                >
                  <Input
                    onChange={e =>
                      setFormData(prevState => ({
                        ...prevState,
                        provincia: e.target.value,
                      }))
                    }
                    name="provincia"
                    placeholder="Provincia*"
                    variant={getInputVariant()}
                    required
                    sx={customStyles.input}
                  />
                </Box>
              )}
            </Flex>
          )}

          {showMessageField && (
            <Textarea
              sx={{
                fontFamily: "body",
                fontSize: [2],
                ...customStyles.textarea,
              }}
              onChange={e =>
                setFormData(prevState => ({
                  ...prevState,
                  messaggio: e.target.value,
                }))
              }
              name="messaggio"
              placeholder="Messaggio"
              variant={getInputVariant()}
              type="textarea"
            />
          )}
          <LabeledCheckbox
            checkedCheckbox={e => {
              setFormData(prevState => ({
                ...prevState,
                accordoPrivacyCookies: e,
              }))
            }}
            required={true}
            variant={variant}
            customStyles={customStyles.checkbox}
          >
            <Box>
              <Text
                variant="p"
                sx={{
                  fontSize: [0],
                  color: getTextColor(),
                  ...customStyles.checkboxText,
                }}
              >
                * Acconsento a che i miei dati personali vengano
                utilizzati in accordo con la{" "}
                <InboundLink
                  sx={{ color: getTextColor(), textDecoration: "underline" }}
                  to="/privacy-policy/"
                >
                  privacy
                </InboundLink>{" "}
                e{" "}
                <InboundLink
                  sx={{ color: getTextColor(), textDecoration: "underline" }}
                  to="/cookie-policy/"
                >
                  cookie
                </InboundLink>{" "}
                policy
              </Text>
            </Box>
          </LabeledCheckbox>
          <LabeledCheckbox
            checkedCheckbox={e =>
              setFormData(prevState => ({
                ...prevState,
                accordoNewsLetter: e,
              }))
            }
            variant={variant}
            customStyles={customStyles.checkbox}
          >
            <Box>
              <Text
                variant="p"
                sx={{
                  fontSize: [0],
                  color: getTextColor(),
                  ...customStyles.checkboxText,
                }}
              >
                Acconsento all'uso dei miei dati personali per essere
                aggiornato sui nuovi arrivi, prodotti in esclusiva e per
                le finalità di marketing diretto correlate ai servizi
                offerti e ricevere proposte in linea con i miei
                interessi attraverso l'analisi dei miei precedenti
                acquisti
              </Text>
            </Box>
          </LabeledCheckbox>

          <Button
            type="submit"
            title={
              !formData.accordoPrivacyCookies
                ? "Acconsenti al trattamenti dei dati personali"
                : ""
            }
            sx={{
              mt: [3, 3, 3, 3],
              opacity: 1,
              "&:hover": {
                color: "light",
                svg: {
                  circle: {
                    stroke: "light",
                    strokeWidth: "2px",
                  },
                  use: {
                    fill: "light",
                  },
                  "g#Group": {
                    fill: "light",
                  },
                },
              },
              ...customStyles.button,
            }}
            variant={getButtonVariant()}
            disabled={loading}
          >
            <Flex
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {loading ? "Caricamento..." : buttonText}
              <Flex
                variant={getArrowVariant()}
                sx={{
                  alignItems: "center",
                  alignContent: "center",
                  width: "24px",
                  height: "24px",
                  ml: ["12px"]
                }}
              >
                <ArrowRight />
              </Flex>
            </Flex>
          </Button>
          {success === true ? (
            <Flex sx={{ maxWidth: "600px", ...customStyles.successMessage }}>
              <Heading
                sx={{ my: [4], color: getTextColor() }}
                as="h5"
              >
                Richiesta inviata! Ti ricontatteremo al più presto.
              </Heading>
            </Flex>
          ) : (
            success === false && (
              <Flex sx={{ maxWidth: "600px", ...customStyles.errorMessage }}>
                <Heading
                  sx={{
                    my: [4],
                    color: getTextColor(),
                  }}
                  as="h5"
                >
                  Qualcosa è andato storto
                </Heading>
              </Flex>
            )
          )}
        </Grid>
      </Box>
    </Box>
  )
}

const LabeledCheckbox = ({
  children,
  defaultChecked,
  variant = "primary",
  checkedCheckbox,
  customStyles = {},
  ...props
}) => {
  const [checked, setChecked] = useState(defaultChecked)

  const getTextColor = () => {
    if (variant === "light") return "dark"
    if (variant === "dark") return "dark"
    return "light"
  }

  return (
    <Label
      sx={{
        display: "flex",
        alignItems: "center",
        fontWeight: checked ? "bold" : "normal",
        color: getTextColor(),
        a: {
          color: getTextColor(),
        },
        "input:checked~.css-kydphz": {
          color: "light",
        },
        svg: {
          color: getTextColor(),
        },
        mt: [3, 3, 3, 3],
        ...customStyles,
      }}
    >
      <Checkbox
        sx={{
          color: variant !== "primary" ? getTextColor() : "light",
          "input:checked~&": {
            color: variant !== "primary" ? getTextColor() : "light",
          },
        }}
        checked={checked}
        onChange={() => {
          checkedCheckbox(!checked)
          setChecked(!checked)
        }}
        {...props}
      />
      {children}
    </Label>
  )
}

// Import Grid from theme-ui to avoid errors
const Grid = props => {
  const { columns, gap, ...rest } = props
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: columns,
        gridGap: gap,
      }}
      {...rest}
    />
  )
}

export default BaseContactForm
